export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "esg-color-scheme";

let preference: ThemePreference = "system";
let initialized = false;
const subscribers = new Set<() => void>();
let cachedSnapshot: { preference: ThemePreference; effective: "light" | "dark" } | null = null;

function systemPrefersDark() {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function readThemePreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    /* localStorage unavailable */
  }
  return "system";
}

export function getThemePreference(): ThemePreference {
  return preference;
}

export function getEffectiveTheme(): "light" | "dark" {
  if (preference === "dark") return "dark";
  if (preference === "light") return "light";
  return systemPrefersDark() ? "dark" : "light";
}

function applyEffectiveTheme() {
  const isDark = getEffectiveTheme() === "dark";
  document.documentElement.classList.toggle("dark", isDark);
  document.documentElement.style.colorScheme = isDark ? "dark" : "light";
  const themeColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--background")
    .trim();
  if (themeColor) {
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", themeColor);
  }
}

function notifySubscribers() {
  subscribers.forEach((listener) => listener());
}

export function setThemePreference(next: ThemePreference) {
  preference = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* localStorage unavailable */
  }
  applyEffectiveTheme();
  notifySubscribers();
}

export function toggleTheme() {
  setThemePreference(getEffectiveTheme() === "dark" ? "light" : "dark");
}

export function subscribeToTheme(listener: () => void) {
  subscribers.add(listener);
  return () => {
    subscribers.delete(listener);
  };
}

export function getThemeSnapshot() {
  const next = {
    preference: getThemePreference(),
    effective: getEffectiveTheme(),
  };
  if (
    cachedSnapshot
    && cachedSnapshot.preference === next.preference
    && cachedSnapshot.effective === next.effective
  ) {
    return cachedSnapshot;
  }
  cachedSnapshot = next;
  return cachedSnapshot;
}

export function initColorScheme() {
  preference = readThemePreference();
  applyEffectiveTheme();

  if (initialized) {
    return () => {};
  }
  initialized = true;

  const media = window.matchMedia("(prefers-color-scheme: dark)");
  const onSystemChange = () => {
    if (preference !== "system") return;
    applyEffectiveTheme();
    notifySubscribers();
  };

  media.addEventListener("change", onSystemChange);
  return () => {
    media.removeEventListener("change", onSystemChange);
    initialized = false;
  };
}

/** Test helper */
export function resetColorSchemeForTests() {
  preference = "system";
  initialized = false;
  cachedSnapshot = null;
  subscribers.clear();
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
